import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Profile } from '../../../types';
import { type TarefaMencionavel } from './mencoes';
import { remarkMencoes, ATRIBUTO_ID, ATRIBUTO_TIPO } from './remarkMencoes.ts';
import { remarkUcs, ATRIBUTO_UC } from './ucNoTexto.ts';
import { UcCitadaBotao, useUcCitada } from './UcCitada';

/**
 * Camada de identificação visual dos comentários (tarefas, solicitações, feed do CRM).
 *
 * Escopo deliberadamente estreito: **não** mexe em layout, espaçamento ou tamanho dos balões —
 * cada tela mantém a marcação que já tinha. O que entra aqui é só:
 *  1. **Menção em negrito** — "@Victor Paulo" sumia no meio do texto corrido; agora vira um chip
 *     destacado, na cor do usuário mencionado.
 *  2. **Cor por autor** — nome (e uma barra lateral fina) numa cor estável por pessoa, pra bater
 *     o olho e saber de quem é cada fala, como no WhatsApp/Telegram em grupo.
 *  3. **Markdown** — o comentário passa pelo mesmo parser da descrição da tarefa, então
 *     `**negrito**`, lista, link e crase funcionam igual. Antes o markdown saía cru: quem
 *     escrevia `**fatura agrupada**` via os asteriscos na tela.
 *
 * A cor sai de um hash do id, não de sorteio — a mesma pessoa tem sempre a mesma cor em todas as
 * telas e entre sessões, que é o que torna o reconhecimento possível.
 *
 * ⚠️ Tipografia continua sendo da tela que chama. Ao contrário do TaskDescriptionMarkdown (que
 * fixa cor e tamanho por elemento, porque lá ele é o dono da área), aqui os componentes só
 * ajustam peso, espaçamento e marcadores — cor e tamanho são herdados do balão. É o que mantém
 * o comentário do feed do CRM em `text-xs` e o da solicitação em `text-sm`, como sempre foram.
 */

export interface PaletaUsuario {
    /** Cor do nome do autor no cabeçalho do comentário. */
    nome: string;
    /** Fundo + texto do chip de menção. */
    chip: string;
    /** Cor da barra lateral do balão (usar junto com `border-l-4`). */
    barra: string;
}

const PALETA: PaletaUsuario[] = [
    { nome: 'text-rose-600 dark:text-rose-400', chip: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300', barra: 'border-l-rose-400 dark:border-l-rose-500' },
    { nome: 'text-sky-600 dark:text-sky-400', chip: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300', barra: 'border-l-sky-400 dark:border-l-sky-500' },
    { nome: 'text-emerald-600 dark:text-emerald-400', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300', barra: 'border-l-emerald-400 dark:border-l-emerald-500' },
    { nome: 'text-amber-600 dark:text-amber-400', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300', barra: 'border-l-amber-400 dark:border-l-amber-500' },
    { nome: 'text-violet-600 dark:text-violet-400', chip: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300', barra: 'border-l-violet-400 dark:border-l-violet-500' },
    { nome: 'text-cyan-600 dark:text-cyan-400', chip: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300', barra: 'border-l-cyan-400 dark:border-l-cyan-500' },
    { nome: 'text-fuchsia-600 dark:text-fuchsia-400', chip: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300', barra: 'border-l-fuchsia-400 dark:border-l-fuchsia-500' },
    { nome: 'text-teal-600 dark:text-teal-400', chip: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300', barra: 'border-l-teal-400 dark:border-l-teal-500' },
    { nome: 'text-orange-600 dark:text-orange-400', chip: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300', barra: 'border-l-orange-400 dark:border-l-orange-500' },
    { nome: 'text-indigo-600 dark:text-indigo-400', chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300', barra: 'border-l-indigo-400 dark:border-l-indigo-500' },
    { nome: 'text-lime-600 dark:text-lime-400', chip: 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300', barra: 'border-l-lime-400 dark:border-l-lime-500' },
    { nome: 'text-pink-600 dark:text-pink-400', chip: 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300', barra: 'border-l-pink-400 dark:border-l-pink-500' },
];

/** Hash estável (djb2) — mesma chave, mesma cor, sempre. */
export const corDoUsuario = (chave?: string | null): PaletaUsuario => {
    const texto = (chave || '').trim();
    if (!texto) return PALETA[0];
    let hash = 5381;
    for (let i = 0; i < texto.length; i++) hash = ((hash << 5) + hash + texto.charCodeAt(i)) >>> 0;
    return PALETA[hash % PALETA.length];
};

// O parsing das menções mora em ./mencoes.ts (função pura, com teste em node:test — o
// type-stripping do Node não lê JSX, então ela não podia ficar aqui). Reexportado para quem já
// importava daqui.
export { partesComMencoes } from './mencoes';
export type { TarefaMencionavel, ParteMencao } from './mencoes';

/** Props que o react-markdown entrega; `node` não pode vazar para o DOM. */
type PropsElemento = { node?: unknown; children?: React.ReactNode } & Record<string, unknown>;

/**
 * Mapa de elementos do comentário.
 *
 * Fora daqui de propósito: não recriar o objeto a cada render de cada comentário — uma lista
 * de 30 comentários remontaria 30 mapas idênticos.
 */
const COMPONENTES = {
    // Cor e tamanho vêm do balão; aqui só o ritmo vertical. `last:mb-0` impede a sobra de
    // espaço embaixo, que empurraria os anexos do comentário para longe do texto.
    p: ({ children }: PropsElemento) => <p className="leading-relaxed mb-2 last:mb-0">{children}</p>,
    strong: ({ children }: PropsElemento) => <strong className="font-bold">{children}</strong>,
    em: ({ children }: PropsElemento) => <em className="italic">{children}</em>,
    del: ({ children }: PropsElemento) => <del className="line-through opacity-70">{children}</del>,

    // `pl-5` em vez de `list-inside`: linha que quebra fica alinhada com a primeira, em vez de
    // voltar para debaixo do marcador.
    ul: ({ children }: PropsElemento) => <ul className="list-disc pl-5 space-y-0.5 mb-2 last:mb-0">{children}</ul>,
    ol: ({ children }: PropsElemento) => <ol className="list-decimal pl-5 space-y-0.5 mb-2 last:mb-0">{children}</ol>,
    li: ({ children }: PropsElemento) => <li className="leading-relaxed">{children}</li>,

    // Título dentro de comentário é ênfase, não capítulo: sobe pouco de tamanho para não
    // estourar o balão.
    h1: ({ children }: PropsElemento) => <h1 className="font-bold text-[1.15em] mt-3 first:mt-0 mb-1">{children}</h1>,
    h2: ({ children }: PropsElemento) => <h2 className="font-bold text-[1.08em] mt-3 first:mt-0 mb-1">{children}</h2>,
    h3: ({ children }: PropsElemento) => <h3 className="font-semibold mt-2 first:mt-0 mb-1">{children}</h3>,
    h4: ({ children }: PropsElemento) => <h4 className="font-semibold mt-2 first:mt-0 mb-1">{children}</h4>,
    h5: ({ children }: PropsElemento) => <h5 className="font-semibold mt-2 first:mt-0 mb-1">{children}</h5>,
    h6: ({ children }: PropsElemento) => <h6 className="font-semibold mt-2 first:mt-0 mb-1">{children}</h6>,

    code: ({ children, className }: PropsElemento) => {
        // Bloco de código vem embrulhado em <pre>, que já põe fundo e cor — aqui só a fonte.
        if (typeof className === 'string' && className.includes('language-')) {
            return <code className={`font-mono ${className}`}>{children}</code>;
        }
        return <code className="bg-black/5 dark:bg-white/10 rounded px-1 py-px font-mono text-[0.9em]">{children}</code>;
    },
    pre: ({ children }: PropsElemento) => (
        <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 rounded-lg p-3 overflow-x-auto text-xs mb-2 last:mb-0">{children}</pre>
    ),
    blockquote: ({ children }: PropsElemento) => (
        <blockquote className="border-l-2 border-primary/40 pl-3 italic opacity-90 mb-2 last:mb-0">{children}</blockquote>
    ),
    hr: () => <hr className="border-gray-200 dark:border-gray-700 my-2" />,
    a: ({ children, href }: PropsElemento) => (
        <a href={typeof href === 'string' ? href : undefined} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">{children}</a>
    ),
    img: ({ src, alt }: PropsElemento) => (
        <img src={typeof src === 'string' ? src : undefined} alt={typeof alt === 'string' ? alt : ''} className="max-w-full rounded-lg my-1" />
    ),

    table: ({ children }: PropsElemento) => (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-2 last:mb-0">
            <table className="w-full border-collapse text-[0.95em]">{children}</table>
        </div>
    ),
    th: ({ children }: PropsElemento) => <th className="px-2 py-1 text-left font-semibold border-b border-gray-200 dark:border-gray-700">{children}</th>,
    td: ({ children }: PropsElemento) => <td className="px-2 py-1 border-b border-gray-100 dark:border-gray-700/50">{children}</td>,

    /**
     * Chips de menção.
     *
     * O `remarkMencoes` marca a menção como um `<span>` com `data-mencao`; qualquer outro span
     * segue o caminho normal. É o que permite a menção sobreviver dentro de negrito, item de
     * lista ou citação, já que ela deixou de competir com o parser de markdown.
     */
    span: ({ children, node, ...resto }: PropsElemento) => {
        const tipo = resto[ATRIBUTO_TIPO];
        // #6587: UC citada, só quando a tela liga o UcCitadaContext.
        if (typeof resto[ATRIBUTO_UC] === 'string') {
            return <UcCitadaBotao numero={resto[ATRIBUTO_UC] as string}>{children}</UcCitadaBotao>;
        }
        if (tipo === 'usuario') {
            const id = typeof resto[ATRIBUTO_ID] === 'string' ? (resto[ATRIBUTO_ID] as string) : undefined;
            return <strong className={`font-bold rounded px-1 py-px ${corDoUsuario(id).chip}`}>{children}</strong>;
        }
        // Chip neutro de propósito: menção de tarefa não é pessoa, não entra na paleta por autor.
        if (tipo === 'tarefa') {
            return <strong className="font-semibold rounded px-1 py-px bg-primary/10 text-primary">{children}</strong>;
        }
        return <span {...resto}>{children}</span>;
    },
};

interface ComentarioTextoProps {
    texto: string;
    usuarios?: Pick<Profile, 'id' | 'full_name'>[];
    /** Tarefas que podem ter sido citadas com "#" (as do projeto atual, por exemplo). */
    tarefas?: TarefaMencionavel[];
    /** Classes do bloco — quem chama continua mandando no tamanho/cor do texto. */
    className?: string;
}

/**
 * Texto do comentário: markdown renderizado, com as menções em destaque.
 */
export const ComentarioTexto: React.FC<ComentarioTextoProps> = ({ texto, usuarios = [], tarefas = [], className }) => {
    // remarkMencoes por ÚLTIMO: o remark-breaks reprocessa nós `text` para virar <br> e
    // desmontaria as menções se rodasse depois delas.
    const ucs = useUcCitada()?.conhecidos;
    const plugins = useMemo(
        () => [remarkGfm, remarkBreaks, remarkMencoes({ usuarios, tarefas }), ...(ucs?.size ? [remarkUcs(ucs)] : [])],
        [usuarios, tarefas, ucs],
    );

    return (
        <div className={`break-words ${className || ''}`}>
            <ReactMarkdown remarkPlugins={plugins as never} components={COMPONENTES as never}>
                {texto}
            </ReactMarkdown>
        </div>
    );
};

export default ComentarioTexto;
